// Category-driven variation schemas — powers the dynamic selectors on the
// product page. Every category may define any combination of:
//   sizes: { region: [...], custom: true }
//   colours: [{name, hex}] with allowCustom: true to enable a custom-colour input
//   fabrics: [ ... ] with allowCustom: true
//   closures / headers / bagTypes / designStyles / monogramFonts — free lists
//   customDesign: true — always show a free-text design brief
//   monogramAddon: true — always show a monogram add-on section
//
// Any product whose `category` matches a schema slug will render the
// variations below on top of the base ones stored on the product record.

const BEDDING_SIZES = {
  US: ["Twin", "Twin XL", "Full", "Queen", "King", "California King"],
  UK: ["Single", "Small Double", "Double", "King", "Super King"],
  IN: ["Single", "Double", "Queen", "King"],
};

const BEDDING_FABRICS = ["Sateen", "Cotton", "Percale", "Linen"];

const BEDDING_COLOURS = [
  { name: "Ivory", hex: "#F5EFE0" },
  { name: "Bone White", hex: "#EFE9DA" },
  { name: "Champagne", hex: "#D4B896" },
  { name: "Sage", hex: "#B7C1AA" },
  { name: "Charcoal", hex: "#2C2C2C" },
  { name: "Forest", hex: "#1C3A2B" },
  { name: "Blush", hex: "#E4C5C0" },
  { name: "Navy", hex: "#1B2A44" },
];

const JACKET_SIZES = ["M", "L", "XL", "2XL", "3XL", "4XL"];

const SOFA_SIZES = ["1 Seater", "2 Seater", "3 Seater", "L-Shape", "Recliner"];
const SOFA_FABRICS = ["Velvet", "Cotton", "Polyester", "Suede", "Linen Blend"];

const CURTAIN_SIZES_IN = [
  '54" × 84"', '54" × 96"', '54" × 108"', '54" × 120"',
  '96" × 84"', '96" × 96"', '96" × 108"', '96" × 120"',
];

const CURTAIN_HEADERS = ["Rod Pocket", "Grommet", "Button Hole", "Back Tab", "Pinch Pleat"];

const CUSHION_SIZES = ['16" × 16"', '18" × 18"', '20" × 20"', '22" × 22"', '24" × 24"', '12" × 20" Lumbar', '14" × 24" Lumbar'];

const MONOGRAM_FONTS = ["Serif Classic", "Script Cursive", "Old English", "Modern Sans", "Vintage Diamond"];

const DRESS_STYLES = ["Western", "Modern", "Ethnic", "Fusion", "Bespoke Custom"];

const BAG_TYPES = ["Hand Bag", "Tote Bag", "Designer Bag", "Clutch", "Custom Design"];

/**
 * Schema is keyed by category slug. Any slug not listed falls back to the
 * product's base sizes/colours/fabric fields with only custom-design +
 * monogram add-on universally applied.
 */
export const VARIATION_SCHEMAS = {
  // -------- Bedding --------
  "duvet-covers": {
    sizes: { regions: BEDDING_SIZES, allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: BEDDING_FABRICS, allowCustom: true },
    closures: ["Zipper", "Buttons", "Envelope"],
    sizeChart: "bedding",
  },
  "sheet-sets": {
    sizes: { regions: BEDDING_SIZES, allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: BEDDING_FABRICS, allowCustom: true },
    sizeChart: "bedding",
  },
  "quilts-bedspreads": {
    sizes: { regions: BEDDING_SIZES, allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: BEDDING_FABRICS, allowCustom: true },
    sizeChart: "bedding",
  },
  "bedskirts": {
    sizes: { regions: BEDDING_SIZES, allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    sizeChart: "bedding",
  },
  "hotel-bedding": {
    sizes: { regions: BEDDING_SIZES, allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: BEDDING_FABRICS, allowCustom: true },
    closures: ["Zipper", "Buttons", "Envelope"],
    sizeChart: "bedding",
  },
  "monogram-bedding": {
    sizes: { regions: BEDDING_SIZES, allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: BEDDING_FABRICS, allowCustom: true },
    closures: ["Zipper", "Buttons", "Envelope"],
    monogramFont: { options: MONOGRAM_FONTS, required: true },
    sizeChart: "bedding",
  },
  "custom-bedding": {
    sizes: { regions: BEDDING_SIZES, allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: BEDDING_FABRICS, allowCustom: true },
    closures: ["Zipper", "Buttons", "Envelope"],
    sizeChart: "bedding",
  },

  // -------- Cushions & Pillows --------
  "pillow-covers": {
    sizes: { list: CUSHION_SIZES, allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: BEDDING_FABRICS, allowCustom: true },
    sizeChart: "cushion",
  },
  "cushion-covers": {
    sizes: { list: CUSHION_SIZES, allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: BEDDING_FABRICS, allowCustom: true },
    sizeChart: "cushion",
  },

  // -------- Curtains --------
  "curtains": {
    sizes: { list: CURTAIN_SIZES_IN, allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: [...BEDDING_FABRICS, "Velvet", "Blackout Weave"], allowCustom: true },
    header: { options: CURTAIN_HEADERS, required: true },
    sizeChart: "curtain",
  },

  // -------- Towels (all sub-types) --------
  "bath": {
    sizes: { list: ["Standard 27\" × 54\"", "Oversized 30\" × 58\"", "Bath Sheet 35\" × 70\""], allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: ["Cotton", "Turkish Cotton", "Egyptian Cotton", "Bamboo"], allowCustom: true },
  },
  "hand-towels": {
    sizes: { list: ["Standard 16\" × 28\"", "Oversized 20\" × 32\""], allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: ["Cotton", "Turkish Cotton", "Bamboo"], allowCustom: true },
  },
  "bath-towels": {
    sizes: { list: ["Standard 27\" × 54\"", "Oversized 30\" × 58\"", "Bath Sheet 35\" × 70\""], allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: ["Cotton", "Turkish Cotton", "Egyptian Cotton", "Bamboo"], allowCustom: true },
  },
  "face-towels": {
    sizes: { list: ["Standard 12\" × 12\"", "Oversized 14\" × 14\""], allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: ["Cotton", "Turkish Cotton", "Bamboo"], allowCustom: true },
  },
  "bath-mats": {
    sizes: { list: ['17" × 24"', '20" × 32"', '24" × 40"'], allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: ["Cotton", "Turkish Cotton", "Chenille"], allowCustom: true },
  },

  // -------- Bags --------
  "handmade-bags": {
    bagType: { options: BAG_TYPES, required: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: ["Canvas", "Jute", "Cotton", "Linen", "Velvet", "Silk"], allowCustom: true },
  },

  // -------- Winter Jackets --------
  "winter-jackets": {
    sizes: { list: JACKET_SIZES, allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: ["Wool", "Down-fill", "Quilted Poly", "Faux Fur Lined"], allowCustom: true },
    sizeChart: "jacket",
  },

  // -------- Sofa Covers --------
  "sofa-covers": {
    sizes: { list: SOFA_SIZES, allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: SOFA_FABRICS, allowCustom: true },
  },

  // -------- Ladies Designer Dress --------
  "linen-dresses": {
    designStyle: { options: DRESS_STYLES, required: true },
    sizes: { list: ["XS", "S", "M", "L", "XL", "2XL"], allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: ["Linen", "Cotton", "Silk", "Chiffon", "Georgette", "Velvet"], allowCustom: true },
    sizeChart: "dress",
  },

  // -------- Throws / Headboards --------
  "throws-blankets": {
    sizes: { list: ['50" × 60"', '60" × 80"', '80" × 100"'], allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: ["Wool", "Cashmere", "Cotton", "Linen", "Faux Fur"], allowCustom: true },
  },
  "headboards": {
    sizes: { regions: BEDDING_SIZES, allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: ["Velvet", "Linen", "Boucle", "Leatherette"], allowCustom: true },
  },

  // -------- Baby Care --------
  "baby-care": {
    sizes: { list: ['Newborn 30" × 40"', 'Crib 40" × 60"', 'Toddler 55" × 70"'], allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: ["Organic Cotton", "Bamboo", "Muslin", "Fleece"], allowCustom: true },
  },

  // -------- Pet Care --------
  "pet-care": {
    sizes: { list: ["Small", "Medium", "Large", "X-Large"], allowCustom: true },
    colours: { options: BEDDING_COLOURS, allowCustom: true },
    fabrics: { options: ["Cotton", "Fleece", "Waterproof Poly", "Faux Fur"], allowCustom: true },
  },

  // -------- Gifts (mostly rely on universal customization) --------
  "handmade-gifts":   { colours: { options: BEDDING_COLOURS, allowCustom: true } },
  "personalised-gifts": { colours: { options: BEDDING_COLOURS, allowCustom: true } },
  "custom-gifts":     { colours: { options: BEDDING_COLOURS, allowCustom: true } },
};

export const SIZE_CHARTS = {
  bedding: {
    title: "Bedding Size Chart",
    columns: ["Size", "US (in)", "UK (in)", "IN (in)"],
    rows: [
      ["Single", "39 × 75", "36 × 75", "36 × 75"],
      ["Twin / Small Double", "39 × 75", "48 × 75", "48 × 75"],
      ["Full / Double", "54 × 75", "54 × 75", "54 × 75"],
      ["Queen", "60 × 80", "60 × 80", "60 × 78"],
      ["King", "76 × 80", "72 × 78", "72 × 78"],
      ["Cal / Super King", "72 × 84", "72 × 84", "—"],
    ],
  },
  jacket: {
    title: "Jacket Size Chart (inches)",
    columns: ["Size", "Chest", "Waist", "Length"],
    rows: [
      ["M", "38–40", "32–34", "27"],
      ["L", "41–43", "35–37", "28"],
      ["XL", "44–46", "38–40", "29"],
      ["2XL", "47–49", "41–43", "30"],
      ["3XL", "50–52", "44–46", "31"],
      ["4XL", "53–55", "47–49", "32"],
    ],
  },
  curtain: {
    title: "Curtain Size Guide (inches)",
    columns: ["Panel width", "Recommended drop", "Fits window"],
    rows: [
      ['54"', '84" / 96"', "Up to 48 in"],
      ['54"', '108" / 120"', "Up to 48 in — floor length"],
      ['96"', '84" / 96"', "48–90 in wide"],
      ['96"', '108" / 120"', "48–90 in — floor length"],
    ],
  },
  cushion: {
    title: "Cushion Size Guide (inches)",
    columns: ["Common name", "Cover size"],
    rows: [
      ["European", '26" × 26"'],
      ["Standard sofa", '18" × 18" — 22" × 22"'],
      ["Lumbar", '12" × 20" — 14" × 24"'],
    ],
  },
  dress: {
    title: "Dress Size Chart (inches)",
    columns: ["Size", "Bust", "Waist", "Hip"],
    rows: [
      ["XS", "32", "25", "35"],
      ["S", "34", "27", "37"],
      ["M", "36", "29", "39"],
      ["L", "38", "31", "41"],
      ["XL", "40", "33", "43"],
      ["2XL", "42", "35", "45"],
    ],
  },
};

export const getSchema = (categorySlug) => VARIATION_SCHEMAS[categorySlug] || {};
